import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Hides monetary values across the studios pages — rates, costs, amounts due and
 * paid — so the back office can be shown on a shared screen or in a meeting
 * without exposing what each studio is paid. The choice is per-browser and
 * cosmetic: the values are still fetched, so this is a privacy convenience, not
 * an access control.
 */
interface MoneyVisibilityValue {
  hidden: boolean;
  toggle: () => void;
  /** Format a USD amount, masked when hiding is on. `null` renders as a dash. */
  money: (value: number | null | undefined, opts?: { decimals?: number; suffix?: string }) => string;
}

const STORAGE_KEY = 'samawy-ui-hide-money';
const MASK = '•••••';

const MoneyVisibilityContext = createContext<MoneyVisibilityValue | null>(null);

export function MoneyVisibilityProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
  }, [hidden]);

  const value = useMemo<MoneyVisibilityValue>(() => ({
    hidden,
    toggle: () => setHidden((current) => !current),
    money: (amount, opts) => {
      if (hidden) return MASK;
      if (amount == null) return '—';
      return `$${amount.toFixed(opts?.decimals ?? 2)}${opts?.suffix ?? ''}`;
    },
  }), [hidden]);

  return <MoneyVisibilityContext.Provider value={value}>{children}</MoneyVisibilityContext.Provider>;
}

export function useMoneyVisibility() {
  const context = useContext(MoneyVisibilityContext);
  if (!context) throw new Error('useMoneyVisibility must be used within a MoneyVisibilityProvider');
  return context;
}

/** The show/hide control. Placed in the header of each studios page. */
export function MoneyVisibilityToggle({ isArabic }: { isArabic: boolean }) {
  const { hidden, toggle } = useMoneyVisibility();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={hidden}
      title={hidden
        ? (isArabic ? 'إظهار القيم المالية' : 'Show monetary values')
        : (isArabic ? 'إخفاء القيم المالية عن الشاشة' : 'Hide monetary values from the screen')}
      className={`btn-secondary text-xs py-1.5 px-2.5 inline-flex items-center gap-1.5 ${hidden ? 'bg-slate-100 text-slate-700' : ''}`}
    >
      {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      {hidden ? (isArabic ? 'إظهار المبالغ' : 'Show amounts') : (isArabic ? 'إخفاء المبالغ' : 'Hide amounts')}
    </button>
  );
}
